import {
  appliDuPortail,
  collecteDeDocuments,
  connexionProposee,
  formaterMontant,
  type PieceEnVue,
  type StatutCollecte,
} from '@admin-pmvb/core'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate } from '@tanstack/react-router'
import type { InferResponseType } from 'hono/client'
import { type ReactNode, useEffect, useRef, useState } from 'react'
import { api, ErreurApi, lire, requetes } from '../api'
import { LIBELLES_ACTION_JUSTIFICATIF, LIBELLES_NATURE_OPERATION } from '../libelles'
import { PagesDuFichier } from './ApercuPiece'
import { ACCEPT_FICHIERS, RecapDepot, SuiviDepot, useDepot } from './DepotPieces'
import {
  type BilanConnexion,
  type Decisions,
  detailsDeLaVue,
  type OperationBilan,
  type PieceResumee,
  PiecesSansOperation,
  type Recherches,
  Registre,
  requeteBilan,
  ReleveDuDebit,
  SansLiaison,
  useDecisions,
  useRecherches,
  vueSeule,
} from './BilanJustificatifs'
import {
  aideListe,
  aideOnglet,
  candidatesTriees,
  type ConnecteurVu,
  confirmationChoisir,
  type ContexteListes,
  type ecartCandidate,
  etatChoix,
  etatConnecteur,
  gesteDejaDansLePortail,
  jourCourt,
  libelleRecuperer,
  libelleScanMails,
  type ListeBilan,
  listesDuBilan,
  nomConnecteur,
  ecrituresDuConnecteur,
  ONGLETS_BILAN,
  type OngletBilan,
  ongletsDuBilan,
  origineJustifiee,
  peutChercher,
  peutChercherUnePiece,
  peutChoisir,
  pieceCherchable,
  TERMINEES_MONTREES,
  termineesCoupees,
  texteChoix,
  texteOrigine,
  texteRecherche,
  texteRecuperation,
  texteSansChoix,
  titreOnglet,
  titrePieceRetenue,
  type TonEcart,
} from './bilan-justificatifs'
import { formaterInstant } from './Historique'
import { Icone } from './Icone'

// Le bilan des justificatifs d'une connexion (Tiime, Dougs), refondu le 14/10/2026 d'après la
// maquette « Justificatifs Tiime v2 » : quatre onglets (à justifier, prêtes à relier, justifiées,
// écartées) ; à gauche, les écritures du cabinet en tableau dense (date, fournisseur et libellé,
// meilleure pièce en deux pastilles d'écart, montant ; J et K pour passer de l'une à l'autre) ; à
// droite, collant au défilement, les pièces de l'appli pour l'écriture choisie, de la plus proche
// à la moins proche, chacune avec ses deux tuiles d'écart (montant, date), « Voir » et « C'est
// celle-ci » ; les recherches (« Scanner les boîtes mails », « Récupérer via <connecteur> ») ;
// « Déposer le fichier », « Pas de justificatif attendu », « Voir dans Tiime ». L'aperçu montre le
// vrai fichier, l'écriture et les tuiles ; ← → d'une candidate à l'autre, Échap ferme. Sous la
// grille, repliées : pièces sans opération, justificatifs sans liaison, registre. Aucun bouton
// n'écrit chez le portail : « C'est celle-ci » est une décision que l'agent du Mac exécute au
// passage suivant en écriture ; « Récupérer » lance la collecte à la demande d'un connecteur, à
// ses règles.

type Connexion = InferResponseType<typeof api.portails.$get, 200>['connexions'][number]

/** Une collecte d'un connecteur de documents, suivie dans le panneau de toutes ses écritures. */
type Recuperation = { connexionId: string; collecteId: string }

/** La pièce telle que les tuiles d'écart la lisent : sa devise lue, sinon celle de la pièce. */
const pourEcart = (p: PieceResumee) => ({
  jour: p.jour,
  montantCts: p.montantCts,
  devise: p.deviseLue ?? p.devise,
})

/** Les pièces en vue d'une opération, selon sa liste : candidates, pièce retenue ou retrouvées. */
function vuesDe(op: OperationBilan, liste: ListeBilan): readonly PieceEnVue[] {
  const retenue = (id: string) => op.candidates.find((c) => c.pieceId === id) ?? vueSeule(id)
  switch (liste) {
    case 'relierait': {
      // Choisie par un humain : la pièce choisie seule, parmi les candidates de la règle.
      const choisie = op.choix && op.pieceId === op.choix.pieceId ? op.pieceId : null
      return choisie ? [retenue(choisie)] : op.candidates
    }
    case 'reliee':
      return op.pieceId ? [retenue(op.pieceId)] : []
    case 'deja':
      return op.pieceId ? [retenue(op.pieceId)] : op.candidates
    case 'mails':
      return op.piecesMails.map(vueSeule)
    case 'a_trancher':
    case 'a_fournir':
      // Choisie par la recherche, hors des candidates : montrée avec elles.
      return op.choix && !op.candidates.some((c) => c.pieceId === op.choix!.pieceId)
        ? [...op.candidates, vueSeule(op.choix.pieceId)]
        : op.candidates
    case 'ecartee':
    case 'hors_champ':
      return []
  }
}

/** Les vues d'une opération, triées de la plus proche à la moins proche. */
function vuesTriees(b: BilanConnexion, op: OperationBilan, liste: ListeBilan) {
  return candidatesTriees(op, vuesDe(op, liste), (id) => {
    const p = b.pieces[id]
    return p ? pourEcart(p) : undefined
  })
}

type VueTriee = ReturnType<typeof vuesTriees>[number]

/** L'onglet ouvert d'abord : « À justifier » s'il a des lignes, sinon le premier qui en a. */
function ongletDeDepart(compte: (o: OngletBilan) => number): OngletBilan {
  return ONGLETS_BILAN.find((o) => compte(o) > 0) ?? 'a_justifier'
}

/** Un texte de recherche (fournisseur, libellé, montant) qui correspond à l'opération. */
function correspond(op: OperationBilan, filtre: string): boolean {
  const f = filtre.trim().toLowerCase()
  if (!f) return true
  return [op.marchand ?? '', op.libelle, formaterMontant(op.montantCts, op.devise)]
    .join(' ')
    .toLowerCase()
    .replace(/\u202f/g, ' ')
    .includes(f)
}

export function BilanEcritures({ b, ecriture }: { b: BilanConnexion; ecriture: boolean }) {
  const decisions = useDecisions(b.connexion.id)
  const recherches = useRecherches(b.connexion.id)
  const connexions = useQuery(requetes.portails).data?.connexions ?? []
  const documents = connexions.filter((c) => collecteDeDocuments(c.fournisseur))
  const onglets = ongletsDuBilan(b.operations)
  const o: ContexteListes = {
    portail: b.connexion.portail,
    ecritureOuverte: b.ecriture.active,
    depotPermis: b.passageCalcule?.depotPermis ?? true,
  }
  const [onglet, setOnglet] = useState<OngletBilan>(() => ongletDeDepart((x) => onglets[x].length))
  const [filtre, setFiltre] = useState('')
  const [choisie, setChoisie] = useState<string | null>(null)
  // Par connexion : une collecte relève toutes les factures du connecteur, son résultat vaut pour
  // chacune des écritures qui l'attendent.
  const [recuperations, setRecuperations] = useState<Record<string, Recuperation>>({})
  const [connecteurs, setConnecteurs] = useState<Record<string, string>>({})
  const connecteurDe = (op: OperationBilan) =>
    connecteurs[op.id] ?? connexionProposee(op, documents, b.connexion.entiteId)?.id ?? null
  const toutes = ONGLETS_BILAN.flatMap((x) => onglets[x])
  const ecrituresDe = (connexionId: string) =>
    ecrituresDuConnecteur(toutes, connexionId, connecteurDe)
  const panneau = useRef<HTMLElement>(null)

  const lignes = onglets[onglet].filter((l) => correspond(l.op, filtre))
  const selection = lignes.find((l) => l.op.id === choisie) ?? lignes[0] ?? null
  const ids = useRef<string[]>([])
  ids.current = lignes.map((l) => l.op.id)
  const courante = useRef<string | null>(null)
  courante.current = selection?.op.id ?? null

  // J et K passent d'une écriture à l'autre, hors d'un champ de saisie et d'une fenêtre ouverte.
  useEffect(() => {
    function touche(e: KeyboardEvent) {
      if ((e.key !== 'j' && e.key !== 'k') || e.metaKey || e.ctrlKey || e.altKey) return
      const t = e.target as HTMLElement | null
      if (t && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName))) return
      if (document.querySelector('dialog[open]')) return
      const liste = ids.current
      if (liste.length === 0) return
      const i = liste.indexOf(courante.current ?? '')
      const suivante = liste[Math.max(0, Math.min(liste.length - 1, i + (e.key === 'j' ? 1 : -1)))]
      if (!suivante) return
      e.preventDefault()
      setChoisie(suivante)
      document.getElementById(`ecriture-${suivante}`)?.scrollIntoView({ block: 'nearest' })
    }
    window.addEventListener('keydown', touche)
    return () => window.removeEventListener('keydown', touche)
  }, [])

  function changerOnglet(x: OngletBilan) {
    setOnglet(x)
    setChoisie(null)
  }

  function choisir(id: string) {
    setChoisie(id)
    // Colonnes empilées (téléphone, fenêtre étroite) : le panneau est sous la liste.
    if (window.matchMedia('(max-width: 1100px)').matches)
      panneau.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  const coupees = termineesCoupees(listesDuBilan(b.operations))
  const aide = [
    aideOnglet(onglet, o),
    coupees && (onglet === 'justifiees' || onglet === 'ecartees')
      ? `les ${TERMINEES_MONTREES} plus récentes`
      : null,
  ]
    .filter(Boolean)
    .join(' · ')

  return (
    <>
      {b.passageCalcule && b.passage && b.passageCalcule.collecteId !== b.passage.collecteId && (
        <p className="discret" role="status">
          Les écritures ci-dessous viennent du passage
          {b.passageCalcule.finLe ? ` terminé le ${formaterInstant(b.passageCalcule.finLe)}` : ''} :
          le dernier n’a pas encore de plan.
        </p>
      )}
      {!b.passageCalcule && (
        <p className="discret">Aucun passage calculé : rien à montrer pour l’instant.</p>
      )}
      <div className="barre-outils bilan-barre">
        <div className="onglets" role="tablist" aria-label="Écritures du cabinet">
          {ONGLETS_BILAN.map((x) => (
            <button
              key={x}
              type="button"
              role="tab"
              aria-selected={x === onglet}
              className={`onglet${x === onglet ? ' actif' : ''}`}
              onClick={() => changerOnglet(x)}
            >
              {titreOnglet(x)}
              <span className={`mono${x === onglet ? ' compteur-onglet' : ''}`}>
                {onglets[x].length}
              </span>
            </button>
          ))}
        </div>
        <label className="bilan-filtre">
          <Icone nom="search" />
          <input
            type="search"
            value={filtre}
            onChange={(e) => setFiltre(e.target.value)}
            placeholder="Fournisseur, libellé, montant…"
            aria-label="Filtrer les écritures"
          />
        </label>
      </div>

      <div className="bilan-grille">
        <section className="carte bilan-ecritures" aria-label={`Écritures ${b.connexion.portail}`}>
          <div className="bilan-colonne-titre">
            <span className="bilan-repere repere-cabinet" />
            <h2>Écritures {b.connexion.portail}</h2>
            <span className="discret">{aide}</span>
          </div>
          <div className="ecritures-entete" aria-hidden="true">
            <span>Date</span>
            <span>Fournisseur</span>
            <span>Meilleure pièce</span>
            <span className="montant-colonne">Montant</span>
          </div>
          {lignes.length === 0 ? (
            <p className="discret ecritures-vide">Rien dans cette liste.</p>
          ) : (
            <ul className="ecritures-liste">
              {lignes.map(({ op, liste }) => (
                <li key={op.id}>
                  <LigneEcriture
                    b={b}
                    op={op}
                    liste={liste}
                    active={op.id === selection?.op.id}
                    connexions={documents}
                    onChoisir={() => choisir(op.id)}
                  />
                </li>
              ))}
            </ul>
          )}
          <p className="discret ecritures-clavier">
            <kbd>J</kbd> / <kbd>K</kbd> pour passer d’une écriture à l’autre
          </p>
        </section>

        <section
          ref={panneau}
          className="carte bilan-panneau"
          aria-label={`Pièces de l’appli${selection ? ` pour ${selection.op.marchand ?? selection.op.libelle}` : ''}`}
        >
          {selection ? (
            <PanneauOperation
              key={selection.op.id}
              b={b}
              op={selection.op}
              liste={selection.liste}
              position={`${lignes.indexOf(selection) + 1} / ${lignes.length}`}
              ecriture={ecriture}
              decisions={decisions}
              recherches={recherches}
              documents={documents}
              connecteur={connecteurs[selection.op.id] ?? null}
              onConnecteur={(id) => setConnecteurs((x) => ({ ...x, [selection.op.id]: id }))}
              recuperations={recuperations}
              onRecuperation={(r) => setRecuperations((x) => ({ ...x, [r.connexionId]: r }))}
              ecrituresDe={ecrituresDe}
            />
          ) : (
            <p className="discret bilan-panneau-vide">Choisir une écriture à gauche.</p>
          )}
        </section>
      </div>

      <PiecesSansOperation b={b} />
      <SansLiaison b={b} />
      <Registre b={b} />
    </>
  )
}

/** Une pastille d'écart (montant ou date), de la couleur de son ton. */
function Pastille({ ton, children }: { ton: TonEcart; children: ReactNode }) {
  return <span className={`pastille-ecart ecart-${ton}`}>{children}</span>
}

/** Une ligne du tableau des écritures : date, fournisseur et libellé, meilleure pièce, montant. */
function LigneEcriture({
  b,
  op,
  liste,
  active,
  connexions,
  onChoisir,
}: {
  b: BilanConnexion
  op: OperationBilan
  liste: ListeBilan
  active: boolean
  connexions: readonly Connexion[]
  onChoisir: () => void
}) {
  const portail = b.connexion.portail
  const vues = vuesTriees(b, op, liste)
  const meilleure = vues[0]?.ecart ?? null
  let piece: ReactNode
  const origine = origineJustifiee(liste, portail)
  if (origine)
    piece = (
      <span className="badge grand ton-ok">
        <Icone nom={liste === 'deja' ? 'verified' : 'link'} />
        {origine}
      </span>
    )
  else if (liste === 'ecartee' || liste === 'hors_champ')
    piece = (
      <span className="badge grand">
        <Icone nom="block" />
        {liste === 'ecartee' ? 'Écartée' : 'Hors champ'}
      </span>
    )
  else if (meilleure && (meilleure.montant || meilleure.date))
    piece = (
      <span className="meilleure-piece">
        {meilleure.montant && (
          <Pastille ton={meilleure.montant.ton}>{meilleure.montant.texte}</Pastille>
        )}
        {meilleure.date && <Pastille ton={meilleure.date.ton}>{meilleure.date.texte}</Pastille>}
        {vues.length > 1 && <span className="discret">+{vues.length - 1}</span>}
      </span>
    )
  else {
    const c = connexionProposee(op, connexions, b.connexion.entiteId)
    piece = c ? (
      <span className="badge grand ton-pointille">
        <Icone nom="hub" />
        Récupérer via {nomConnecteur(c.fournisseurLibelle)}
      </span>
    ) : (
      <span className="badge grand ton-ambre">
        <Icone nom="help" />
        Aucune pièce
      </span>
    )
  }
  const libelle = op.marchand && op.libelle !== op.marchand ? op.libelle : null
  return (
    <button
      type="button"
      id={`ecriture-${op.id}`}
      className={`ligne-ecriture${active ? ' active' : ''}`}
      aria-current={active ? 'true' : undefined}
      onClick={onChoisir}
    >
      <span className="ecriture-date mono">
        <span>{jourCourt(op.date)}</span>
        <span className="discret">{op.date.slice(0, 4)}</span>
      </span>
      <span className="ecriture-fournisseur">
        <strong>{op.marchand ?? op.libelle}</strong>
        {libelle && <span className="mono discret">{libelle}</span>}
      </span>
      <span className="ecriture-piece">{piece}</span>
      <span className="ecriture-montant mono">{formaterMontant(op.montantCts, op.devise)}</span>
    </button>
  )
}

/** Les deux tuiles d'écart d'une candidate : montant (barre du centre), date (axe de ±31 jours). */
function TuilesEcart({
  ecart,
  piece,
  devise,
}: {
  ecart: ReturnType<typeof ecartCandidate>
  piece: PieceResumee | undefined
  devise: string
}) {
  const { montant, date } = ecart
  return (
    <div className="tuiles-ecart">
      {montant ? (
        <div className={`tuile-ecart ecart-${montant.ton}`}>
          <span className="tuile-ecart-titre">
            Écart montant
            <IconeTon ton={montant.ton} />
          </span>
          <span className="tuile-ecart-valeur mono">{montant.texte}</span>
          <span className="tuile-ecart-axe" aria-hidden="true">
            <span className="tuile-ecart-centre" />
            <span
              className="tuile-ecart-barre"
              style={{ left: `${montant.barre.gauche}%`, width: `${montant.barre.largeur}%` }}
            />
          </span>
          <span className="tuile-ecart-detail mono">{montant.detail}</span>
        </div>
      ) : (
        <div className="tuile-ecart ecart-neutre">
          <span className="tuile-ecart-titre">Écart montant</span>
          <span className="tuile-ecart-valeur mono">
            {piece?.montantCts === null || !piece ? '?' : (piece.deviseLue ?? piece.devise)}
          </span>
          <span className="tuile-ecart-detail">
            {piece?.montantCts === null || !piece
              ? 'Montant de la pièce non lu'
              : `${formaterMontant(Math.abs(piece.montantCts), piece.deviseLue ?? piece.devise)} pour une écriture en ${devise}`}
          </span>
        </div>
      )}
      {date && (
        <div className={`tuile-ecart ecart-${date.ton}`}>
          <span className="tuile-ecart-titre">
            Écart date
            <IconeTon ton={date.ton} />
          </span>
          <span className="tuile-ecart-valeur mono">{date.texte}</span>
          <span className="tuile-ecart-axe" aria-hidden="true">
            <span className="tuile-ecart-centre" />
            <span className="tuile-ecart-point" style={{ left: `${date.position}%` }} />
          </span>
          <span className="tuile-ecart-detail mono">{date.detail}</span>
        </div>
      )}
    </div>
  )
}

function IconeTon({ ton }: { ton: TonEcart }) {
  return (
    <Icone
      nom={ton === 'loin' ? 'cancel' : ton === 'ambre' ? 'error' : 'check_circle'}
      plein
      taille={16}
    />
  )
}

/**
 * Le panneau d'une écriture : son rappel, les recherches, le choix fait, ce que dit sa liste, les
 * pièces de la plus proche à la moins proche, puis les gestes du pied (déposer, écarter, voir chez
 * le portail).
 */
function PanneauOperation({
  b,
  op,
  liste,
  position,
  ecriture,
  decisions,
  recherches,
  documents,
  connecteur,
  onConnecteur,
  recuperations,
  onRecuperation,
  ecrituresDe,
}: {
  b: BilanConnexion
  op: OperationBilan
  liste: ListeBilan
  position: string
  ecriture: boolean
  decisions: Decisions
  recherches: Recherches
  documents: readonly Connexion[]
  connecteur: string | null
  onConnecteur: (connexionId: string) => void
  recuperations: Readonly<Record<string, Recuperation>>
  onRecuperation: (r: Recuperation) => void
  ecrituresDe: (connexionId: string) => number
}) {
  const portail = b.connexion.portail
  const o = {
    portail,
    ecritureOuverte: b.ecriture.active,
    depotPermis: b.passageCalcule?.depotPermis ?? true,
  }
  const [apercu, setApercu] = useState<number | null>(null)
  const vues = vuesTriees(b, op, liste)
  const occupe = decisions.enCours !== null || recherches.enCours !== null
  const erreur =
    decisions.erreur?.operationId === op.id
      ? decisions.erreur.message
      : recherches.erreur?.operationId === op.id
        ? recherches.erreur.message
        : null
  const aJustifier = liste === 'a_trancher' || liste === 'a_fournir' || liste === 'mails'
  const choix = texteChoix(
    {
      ...op,
      choix: op.choix && {
        ...op.choix,
        le: op.choix.le ? formaterInstant(op.choix.le) : null,
      },
    },
    { ecritureOuverte: b.ecriture.active, libelleMotif: op.libelleMotif },
  )
  const annulable = ecriture && etatChoix(op) !== null
  // Écarter : une opération sans justificatif chez le portail, que la règle ne relie pas seule.
  const ecartable =
    ecriture && op.decision !== 'ecartee' && op.justificatifsSource === 0 && aJustifier
  const appli = appliDuPortail(b.connexion.fournisseur)

  /**
   * Le geste proposé sur une pièce en vue, selon la liste ; null : aucun. `apres` : une fois la
   * décision prise (confirmée et enregistrée), pour fermer l'aperçu.
   */
  function geste(pieceId: string, principal: boolean, apres?: () => void): ReactNode {
    // « C'est celle-ci » : le choix vaut décision, l'agent du Mac relie au prochain passage.
    const decider = async (d: Parameters<Decisions['decider']>[0], confirmation?: string) => {
      if (await decisions.decider(d, confirmation)) apres?.()
    }
    if (!ecriture) return null
    const depot = b.pieces[pieceId]?.depot
    if (liste === 'a_trancher' && peutChoisir(op, { pieceId, depot }))
      return (
        <button
          type="button"
          className={principal ? 'bouton' : 'bouton-discret'}
          disabled={occupe}
          onClick={() =>
            void decider(
              { operationId: op.id, choix: 'choisir', pieceId },
              confirmationChoisir({ portail, releveARapprocher: op.releve?.aRapprocher ?? false }),
            )
          }
        >
          <Icone nom="check" />
          C’est celle-ci
        </button>
      )
    if (liste === 'deja') {
      const g = gesteDejaDansLePortail({
        retenue: op.pieceId === pieceId,
        reconnue: op.reconnue,
        depot,
      })
      if (g === 'contester')
        return (
          <button
            type="button"
            className="bouton-discret"
            disabled={occupe}
            onClick={() =>
              void decider(
                { operationId: op.id, choix: 'contester', pieceId },
                `Ce n’est pas la pièce de cette opération ? Elle ne lui sera plus proposée et ne sera plus marquée « déjà dans ${portail} ».`,
              )
            }
          >
            Ce n’est pas cette pièce
          </button>
        )
      if (g === 'reconnaitre')
        return (
          <button
            type="button"
            className="bouton-discret"
            disabled={occupe}
            onClick={() =>
              void decider(
                { operationId: op.id, choix: 'reconnaitre', pieceId },
                `Cette pièce est-elle bien celle déjà dans ${portail} pour cette opération ? Elle y sera marquée « déjà dans ${portail} » et ne sera jamais déposée.`,
              )
            }
          >
            C’est celle-ci, déjà dans {portail}
          </button>
        )
    }
    return null
  }

  const info = infoDeLaListe(b, op, liste, o, ecriture)
  const titreVues =
    liste === 'deja' && op.pieceId
      ? titrePieceRetenue(op.reconnue, portail, op.arrivee)
      : liste === 'mails'
        ? 'Retrouvées dans les mails'
        : vues.length > 1
          ? `${vues.length} pièces · la plus proche en premier`
          : vues.length === 1
            ? 'Pièce'
            : null
  const recherche = texteRecherche(op, {
    rechercheLe: op.rechercheLe ? formaterInstant(op.rechercheLe) : null,
    demandeeLe: op.rechercheDemandeeLe ? formaterInstant(op.rechercheDemandeeLe) : null,
  })

  return (
    <>
      <div className="panneau-tete">
        <div className="bilan-colonne-titre">
          <span className="bilan-repere repere-appli" />
          <h2>Pièces admin-pmvb</h2>
          <span className="discret">pour</span>
          <span className="mono discret panneau-position">{position}</span>
        </div>
        <div className="panneau-rappel">
          <Icone nom="receipt_long" />
          <strong>{op.marchand ?? op.libelle}</strong>
          <span className="mono">{jourCourt(op.date, true)}</span>
          <span className="mono panneau-rappel-montant">
            {formaterMontant(op.montantCts, op.devise)}
          </span>
        </div>
        <div className="discret panneau-details">
          {[
            op.marchand && op.libelle !== op.marchand ? op.libelle : null,
            LIBELLES_NATURE_OPERATION[op.nature],
            op.ventilee ? 'ventilée' : null,
            op.provisoire ? 'pas encore réalisée' : null,
            op.validee ? `validée dans ${portail}` : null,
            op.justificatifsSource > 0
              ? `${op.justificatifsSource} justificatif${op.justificatifsSource > 1 ? 's' : ''} dans ${portail}`
              : null,
          ]
            .filter(Boolean)
            .join(' · ')}
        </div>
        {op.libelleMotif && <div className="discret bilan-motif">{op.libelleMotif}</div>}
        {op.releve && <ReleveDuDebit releve={op.releve} />}
        {ecriture && aJustifier && !op.choix && (
          <RecherchesDePiece
            b={b}
            op={op}
            liste={liste}
            recherches={recherches}
            occupe={occupe}
            documents={documents}
            connecteur={connecteur}
            onConnecteur={onConnecteur}
            recuperations={recuperations}
            onRecuperation={onRecuperation}
            ecrituresDe={ecrituresDe}
          />
        )}
        {recherche && <p className="discret panneau-message">{recherche}</p>}
      </div>

      {choix && (
        <div className="panneau-choix">
          <Icone nom="check_circle" plein />
          <span>{choix}</span>
          {annulable && (
            <button
              type="button"
              className="bouton-discret"
              disabled={occupe}
              onClick={() =>
                void decisions.decider(
                  { operationId: op.id, choix: 'rouvrir' },
                  'Annuler ce choix ? L’opération redevient à choisir ; l’agent ne la reliera pas. Un rapprochement bancaire déjà fait reste : il s’annule depuis le relevé.',
                )
              }
            >
              Annuler
            </button>
          )}
        </div>
      )}
      {info && (
        <div className="panneau-info">
          <Icone nom={info.icone} />
          <span>{info.texte}</span>
          {info.rouvrir && (
            <button
              type="button"
              className="bouton-discret"
              disabled={occupe}
              onClick={() => void decisions.decider({ operationId: op.id, choix: 'rouvrir' })}
            >
              Rouvrir
            </button>
          )}
        </div>
      )}

      {ecriture && aJustifier && peutChercherUnePiece(op) && (
        <RecherchePiece b={b} op={op} occupe={occupe} decisions={decisions} />
      )}

      <div className="panneau-pieces">
        {titreVues && <span className="panneau-sous-titre">{titreVues}</span>}
        {vues.length === 0 && aJustifier && (
          <div className="panneau-sans-piece">
            <Icone nom="find_in_page" taille={26} />
            <strong>Aucune pièce candidate</strong>
            <span className="discret">
              Chercher une pièce, lancer une recherche dans les mails ou le connecteur, ou déposer
              le fichier.
            </span>
          </div>
        )}
        <ul className="candidates">
          {vues.map((v, i) => (
            <Candidate
              key={v.pieceId}
              b={b}
              op={op}
              vue={v}
              choisie={op.choix?.pieceId === v.pieceId}
              geste={geste(v.pieceId, i === 0)}
              onVoir={() => setApercu(i)}
            />
          ))}
        </ul>
      </div>

      {erreur && <p className="alerte panneau-message">{erreur}</p>}
      <PiedDuPanneau
        b={b}
        op={op}
        deposer={ecriture && aJustifier && !op.choix && b.connexion.entiteId !== null}
        ecarter={
          ecartable && (
            <button
              type="button"
              className="bouton-discret"
              disabled={occupe}
              onClick={() => void decisions.decider({ operationId: op.id, choix: 'ecarter' })}
            >
              <Icone nom="block" />
              Pas de justificatif attendu
            </button>
          )
        }
        appli={appli}
      />

      {apercu !== null && vues[apercu] && (
        <ApercuCandidates
          b={b}
          op={op}
          vues={vues}
          index={apercu}
          onIndex={setApercu}
          onFermer={() => setApercu(null)}
          // Dans l'aperçu, le geste ferme la fenêtre une fois la décision prise.
          geste={(id) => geste(id, true, () => setApercu(null))}
          choisie={(id) => op.choix?.pieceId === id}
        />
      )}
    </>
  )
}

/**
 * Ce que dit la liste d'une écriture qui n'attend pas de pièce : sûre (l'agent la reliera),
 * reliée par l'agent, déjà chez le portail, retrouvée dans les mails, écartée (« Rouvrir »), hors
 * champ ; à choisir pour qui n'écrit pas sur l'entité : la relier à la main.
 */
function infoDeLaListe(
  b: BilanConnexion,
  op: OperationBilan,
  liste: ListeBilan,
  o: ContexteListes,
  ecriture: boolean,
): {
  texte: string
  icone: 'smart_toy' | 'link' | 'verified' | 'block' | 'mail' | 'rule'
  rouvrir: boolean
} | null {
  const portail = b.connexion.portail
  switch (liste) {
    case 'relierait':
      return {
        texte: [
          op.action ? `${LIBELLES_ACTION_JUSTIFICATIF[op.action]}.` : null,
          aideListe(liste, o),
        ]
          .filter(Boolean)
          .join(' '),
        icone: 'smart_toy',
        rouvrir: false,
      }
    case 'reliee':
      return { texte: aideListe(liste, o), icone: 'link', rouvrir: false }
    case 'deja':
      return {
        texte: op.pieceId
          ? `Justifiée dans ${portail}. Rien à faire.`
          : `Justifiée directement dans ${portail}, hors de l’appli. Rien à faire.`,
        icone: 'verified',
        rouvrir: false,
      }
    case 'mails':
      return { texte: aideListe(liste, o), icone: 'mail', rouvrir: false }
    case 'ecartee':
      return {
        texte: aideListe(liste, o),
        icone: 'block',
        rouvrir: ecriture && op.decision === 'ecartee',
      }
    case 'hors_champ':
      return { texte: aideListe(liste, o), icone: 'block', rouvrir: false }
    case 'a_trancher': {
      const sansChoix = texteSansChoix(op, portail)
      if (sansChoix) return { texte: sansChoix, icone: 'rule', rouvrir: false }
      return ecriture
        ? null
        : {
            texte: `À relier à la main dans ${portail}, ou à choisir par une personne qui écrit sur l’entité : le passage suivant la reconnaît.`,
            icone: 'rule',
            rouvrir: false,
          }
    }
    case 'a_fournir':
      return null
  }
}

/** Une pièce en vue : miniature, émetteur, numéro et origine, « Voir », le geste, les tuiles. */
function Candidate({
  b,
  op,
  vue,
  choisie,
  geste,
  onVoir,
}: {
  b: BilanConnexion
  op: OperationBilan
  vue: VueTriee
  choisie: boolean
  geste: ReactNode
  onVoir: () => void
}) {
  const p = b.pieces[vue.pieceId]
  const origine = p ? texteOrigine(p.origine) : null
  const details = detailsDeLaVue(vue, op, p)
  return (
    <li className={`candidate${choisie ? ' choisie' : ''}`}>
      <div className="candidate-tete">
        <button
          type="button"
          className="candidate-miniature"
          onClick={onVoir}
          aria-label="Voir la pièce"
          title="Voir la pièce"
        >
          <Icone nom="description" />
        </button>
        <div className="candidate-texte">
          <button type="button" className="candidate-nom" onClick={onVoir}>
            {p?.emetteur ?? p?.nom ?? 'Pièce'}
          </button>
          <span className="discret candidate-sous">
            {p?.numero && <span className="mono">n° {p.numero}</span>}
            {origine && (
              <>
                <Icone nom={origine.icone} taille={14} />
                <span>{origine.texte}</span>
              </>
            )}
          </span>
        </div>
        <button type="button" className="bouton-discret" onClick={onVoir}>
          <Icone nom="visibility" />
          Voir
        </button>
        {choisie ? (
          <span className="candidate-choisie">
            <Icone nom="check_circle" plein />
            Choisie
          </span>
        ) : (
          geste
        )}
      </div>
      {vue.ecart && <TuilesEcart ecart={vue.ecart} piece={p} devise={op.devise} />}
      {details && <p className="discret candidate-details">{details}</p>}
    </li>
  )
}

/** Le pied du panneau : déposer le fichier, écarter, voir chez le portail. */
function PiedDuPanneau({
  b,
  op,
  deposer,
  ecarter,
  appli,
}: {
  b: BilanConnexion
  op: OperationBilan
  deposer: boolean
  ecarter: ReactNode
  appli: string | null
}) {
  const client = useQueryClient()
  const depot = useDepot()
  const portail = b.connexion.portail
  const entiteId = b.connexion.entiteId

  return (
    <div className="panneau-pied">
      {deposer && (
        <label className={`bouton-discret depot-fichier${depot.enCours ? ' occupe' : ''}`}>
          <Icone nom="upload_file" />
          Déposer le fichier
          <input
            type="file"
            multiple
            accept={ACCEPT_FICHIERS}
            disabled={depot.enCours}
            onChange={(e) => {
              depot.choisir([...(e.target.files ?? [])])
              e.target.value = ''
            }}
          />
        </label>
      )}
      {ecarter}
      {appli && (
        <a className="panneau-portail" href={appli} target="_blank" rel="noreferrer">
          <Icone nom="open_in_new" />
          Voir dans {portail}
        </a>
      )}
      <div className="panneau-pied-depot">
        <RecapDepot
          depot={depot}
          contexte={`Pour l’écriture ${portail} du ${jourCourt(op.date, true)} : ${op.marchand ?? op.libelle}, ${formaterMontant(Math.abs(op.montantCts), op.devise)}.`}
          surEnvoyer={() =>
            void depot.envoyer({
              ...(entiteId && { entiteId }),
              indication: `Justificatif de l’opération ${portail} du ${jourCourt(op.date, true)} : ${op.marchand ?? op.libelle}, ${formaterMontant(Math.abs(op.montantCts), op.devise)}.`,
            })
          }
        />
        <SuiviDepot
          depot={depot}
          // Lue et rattachée, la pièce rejoint cette écriture : le bilan suit.
          surAvance={() =>
            void client.invalidateQueries({ queryKey: requeteBilan(b.connexion.id).queryKey })
          }
        />
      </div>
    </div>
  )
}

/**
 * Chercher une pièce parmi toutes les factures de l'entité, candidate ou non (décision du
 * 31/10/2026) : « C'est celle-ci » vaut décision, comme pour une candidate ; l'agent la relie au
 * passage suivant. Une pièce déjà chez le cabinet se reconnaît, elle ne se choisit pas.
 */
function RecherchePiece({
  b,
  op,
  occupe,
  decisions,
}: {
  b: BilanConnexion
  op: OperationBilan
  occupe: boolean
  decisions: Decisions
}) {
  const [saisie, setSaisie] = useState('')
  const [terme, setTerme] = useState('')
  useEffect(() => {
    const minuteur = window.setTimeout(() => setTerme(saisie.trim()), 300)
    return () => window.clearTimeout(minuteur)
  }, [saisie])
  const entiteId = b.connexion.entiteId
  const trouvees = useQuery({
    ...requetes.pieces({
      vue: 'toutes',
      q: terme,
      ...(entiteId && { entiteId }),
    }),
    enabled: terme.length >= 2 && entiteId !== null,
  })
  const montant = Math.abs(op.montantCts)
  const lignes = (trouvees.data ?? []).filter((p) => pieceCherchable(p))
  const portail = b.connexion.portail
  return (
    <div className="panneau-chercher">
      <label className="filtre">
        <Icone nom="search" />
        <input
          type="search"
          aria-label="Chercher une pièce"
          placeholder="Chercher une pièce : fournisseur, numéro, mot du texte…"
          value={saisie}
          onChange={(e) => setSaisie(e.target.value)}
        />
      </label>
      {terme.length >= 2 && trouvees.isFetching && <p className="discret">Recherche…</p>}
      {terme.length >= 2 && trouvees.data && lignes.length === 0 && (
        <p className="discret">Aucune facture de cette entité ne correspond.</p>
      )}
      {lignes.length > 0 && (
        <ul className="panneau-trouvees">
          {lignes.map((p) => {
            const total = p.montants?.aPayer ?? p.montants?.ttc ?? null
            return (
              <li key={p.id}>
                <div className="panneau-trouvee-texte">
                  <strong>{p.emetteur ?? p.nomFichier ?? 'Pièce'}</strong>
                  <span className="discret">
                    {[p.numero, p.dateDocument ? jourCourt(p.dateDocument, true) : null]
                      .filter(Boolean)
                      .join(' · ')}
                  </span>
                </div>
                {total !== null && (
                  <span className={total === montant ? 'mono' : 'mono montant-different'}>
                    {formaterMontant(total, p.devise ?? 'EUR')}
                  </span>
                )}
                <a
                  className="bouton-discret"
                  href={`/api/pieces/${p.id}/fichier`}
                  target="_blank"
                  rel="noreferrer"
                >
                  <Icone nom="visibility" />
                  Voir
                </a>
                {p.transmission === 'faite' ? (
                  <span className="discret">Déjà dans {p.transmiseA ?? portail}</span>
                ) : (
                  <button
                    type="button"
                    className="bouton-discret"
                    disabled={occupe}
                    onClick={() =>
                      void decisions.decider(
                        { operationId: op.id, choix: 'choisir', pieceId: p.id },
                        confirmationChoisir({
                          portail,
                          releveARapprocher: op.releve?.aRapprocher ?? false,
                        }) +
                          (total !== null && total !== montant
                            ? ` Attention : la pièce fait ${formaterMontant(total, p.devise ?? 'EUR')}, l’écriture ${formaterMontant(montant, op.devise)}.`
                            : ''),
                      )
                    }
                  >
                    <Icone nom="check" />
                    C’est celle-ci
                  </button>
                )}
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}

/**
 * Les deux recherches d'une écriture à justifier : « Scanner les boîtes mails » (au nom du lecteur,
 * toutes les boîtes qu'il voit et qui couvrent l'entité ; aux règles de `peutChercher`) et
 * « Récupérer via <connecteur> », bouton divisé : le connecteur proposé d'après le fournisseur ou
 * le libellé (`connexionProposee`), la flèche ouvre la liste des connecteurs de documents avec leur
 * état. La récupération passe par la collecte à la demande, à ses règles (le serveur décide).
 */
function RecherchesDePiece({
  b,
  op,
  liste,
  recherches,
  occupe,
  documents,
  connecteur,
  onConnecteur,
  recuperations,
  onRecuperation,
  ecrituresDe,
}: {
  b: BilanConnexion
  op: OperationBilan
  liste: ListeBilan
  recherches: Recherches
  occupe: boolean
  documents: readonly Connexion[]
  connecteur: string | null
  onConnecteur: (connexionId: string) => void
  recuperations: Readonly<Record<string, Recuperation>>
  onRecuperation: (r: Recuperation) => void
  ecrituresDe: (connexionId: string) => number
}) {
  const client = useQueryClient()
  const naviguer = useNavigate()
  const [menu, setMenu] = useState(false)
  const [lance, setLance] = useState(false)
  const [erreur, setErreur] = useState<string | null>(null)
  const zone = useRef<HTMLDivElement>(null)
  const proposee = connexionProposee(op, documents, b.connexion.entiteId)
  const c = documents.find((x) => x.id === connecteur) ?? proposee
  const etat = c
    ? etatConnecteur(c as ConnecteurVu, {
        dernierSucces: c.dernierSuccesLe ? formaterInstant(c.dernierSuccesLe) : null,
      })
    : null
  const nom = c ? nomConnecteur(c.fournisseurLibelle) : null
  // La collecte du connecteur : lancée depuis l'une de ses écritures, ou déjà ouverte (planifiée,
  // lancée ailleurs), suivie de même.
  const recuperation = c ? (recuperations[c.id] ?? null) : null
  const connexionId = c?.id ?? null
  const ouverte = c?.collecte?.id ?? null
  useEffect(() => {
    if (connexionId && ouverte && recuperation?.collecteId !== ouverte)
      onRecuperation({ connexionId, collecteId: ouverte })
  }, [connexionId, ouverte, recuperation?.collecteId, onRecuperation])

  // La collecte lancée : suivie jusqu'à sa fin (historique de la connexion).
  const historique = useQuery({
    queryKey: ['portails', recuperation?.connexionId, 'collectes'],
    queryFn: () =>
      lire(api.portails[':id'].collectes.$get({ param: { id: recuperation!.connexionId } })),
    enabled: recuperation !== null,
    refetchInterval: (q) => {
      const x = q.state.data?.find((l) => l.id === recuperation?.collecteId)
      return !x || ['en_file', 'en_cours', 'attente_humain', 'reprise'].includes(x.statut)
        ? 5000
        : false
    },
  })
  const collecte = historique.data?.find((l) => l.id === recuperation?.collecteId) ?? null
  const fini =
    collecte !== null &&
    !['en_file', 'en_cours', 'attente_humain', 'reprise'].includes(collecte.statut)
  // Une collecte finie : le bilan est relu une fois (les pièces lues rejoignent les candidates),
  // et l'état des connexions (plus de collecte ouverte, dernier succès).
  useEffect(() => {
    if (!fini) return
    void client.invalidateQueries({ queryKey: ['portails', b.connexion.id, 'justificatifs'] })
    void client.invalidateQueries({ queryKey: ['portails'], exact: true })
  }, [fini, client, b.connexion.id])
  const recuperee = recuperation
    ? documents.find((x) => x.id === recuperation.connexionId)
    : undefined
  const resultat =
    recuperation && recuperee
      ? texteRecuperation({
          nom: nomConnecteur(recuperee.fournisseurLibelle),
          statut: (collecte?.statut ?? null) as StatutCollecte | null,
          documentsNouveaux: collecte?.documentsNouveaux ?? null,
          erreur: collecte?.erreur ?? null,
          trouvee: [...op.candidates.map((x) => x.pieceId), ...op.piecesMails].some(
            (id) => b.pieces[id]?.origine.collecteId === recuperation.collecteId,
          ),
          montant: formaterMontant(Math.abs(op.montantCts), op.devise),
          jour: jourCourt(op.date),
        })
      : null

  // La liste se ferme en cliquant ailleurs, ou par Échap.
  useEffect(() => {
    if (!menu) return
    const fermer = (e: MouseEvent) => {
      if (zone.current && !zone.current.contains(e.target as Node)) setMenu(false)
    }
    const echap = (e: KeyboardEvent) => e.key === 'Escape' && setMenu(false)
    document.addEventListener('mousedown', fermer)
    document.addEventListener('keydown', echap)
    return () => {
      document.removeEventListener('mousedown', fermer)
      document.removeEventListener('keydown', echap)
    }
  }, [menu])

  async function recuperer(x: Connexion) {
    setErreur(null)
    setLance(true)
    try {
      const r = await lire(api.portails[':id'].collecter.$post({ param: { id: x.id } }))
      onRecuperation({ connexionId: x.id, collecteId: r.collecteId })
      await client.invalidateQueries({ queryKey: ['portails'], exact: true })
    } catch (e) {
      setErreur(e instanceof ErreurApi ? e.message : 'Collecte impossible.')
    } finally {
      setLance(false)
    }
  }

  function principal() {
    if (!c || !etat) return setMenu(true)
    if (etat.reconnecter && c.defi)
      return void naviguer({ to: '/defi/$id', params: { id: c.defi.id } })
    if (etat.lancer) return void recuperer(c)
    setMenu(true)
  }

  const cherchable =
    (liste === 'a_trancher' || liste === 'a_fournir') && peutChercher(op, b.rechercheMails.motif)
  const scanEnCours = op.rechercheDemandeeLe !== null || recherches.enCours === op.id
  const enCours = lance || (resultat?.enCours ?? false)
  const tries = [...documents].sort(
    (x, y) =>
      Number(y.id === proposee?.id) - Number(x.id === proposee?.id) ||
      Number(y.entiteId === b.connexion.entiteId) - Number(x.entiteId === b.connexion.entiteId) ||
      x.libelle.localeCompare(y.libelle),
  )

  return (
    <div className="panneau-recherches">
      <div className="panneau-recherches-boutons">
        {(liste === 'a_trancher' || liste === 'a_fournir') && (
          <button
            type="button"
            className="bouton-discret bouton-scan"
            disabled={occupe || !cherchable}
            title={b.rechercheMails.libelle ?? undefined}
            onClick={() => void recherches.chercher(op.id)}
          >
            <Icone
              nom={scanEnCours ? 'progress_activity' : 'mail'}
              className={scanEnCours ? 'tourne' : undefined}
            />
            {libelleScanMails({ boites: b.rechercheMails.boites, enCours: scanEnCours })}
          </button>
        )}
        <div className="bouton-divise" ref={zone}>
          <button type="button" className="bouton" disabled={enCours} onClick={principal}>
            <Icone
              nom={enCours ? 'progress_activity' : etat?.reconnecter ? 'link_off' : 'hub'}
              className={enCours ? 'tourne' : undefined}
            />
            <span className="bouton-divise-texte">
              {libelleRecuperer(
                c && nom && etat
                  ? { nom, reconnecter: etat.reconnecter, enCours, ecritures: ecrituresDe(c.id) }
                  : null,
              )}
            </span>
          </button>
          <button
            type="button"
            className="bouton bouton-fleche"
            aria-expanded={menu}
            aria-haspopup="menu"
            title="Changer de connecteur"
            onClick={() => setMenu((x) => !x)}
          >
            <Icone nom="keyboard_arrow_down" />
          </button>
          {menu && (
            <div className="menu-connecteurs" role="menu">
              <span className="menu-connecteurs-titre">Récupérer via</span>
              {tries.length === 0 && (
                <span className="discret menu-connecteurs-vide">
                  Aucun connecteur de documents : en créer un dans{' '}
                  <Link to="/reglages" search={{ onglet: 'connexions', connexion: 'portails' }}>
                    Réglages, Portails
                  </Link>
                  .
                </span>
              )}
              {tries.map((x) => {
                const e = etatConnecteur(x as ConnecteurVu, {
                  dernierSucces: x.dernierSuccesLe ? formaterInstant(x.dernierSuccesLe) : null,
                })
                return (
                  <button
                    key={x.id}
                    type="button"
                    role="menuitemradio"
                    aria-checked={x.id === c?.id}
                    className={`menu-connecteur${x.id === c?.id ? ' actif' : ''}`}
                    onClick={() => {
                      onConnecteur(x.id)
                      setMenu(false)
                    }}
                  >
                    <span className="menu-connecteur-coche">
                      {x.id === c?.id && <Icone nom="check" taille={16} />}
                    </span>
                    <span className="menu-connecteur-nom">
                      {nomConnecteur(x.fournisseurLibelle)}
                      {x.entiteId !== b.connexion.entiteId && x.entite && (
                        <span className="discret"> · {x.entite}</span>
                      )}
                      {x.id === proposee?.id && <span className="discret"> · proposé</span>}
                    </span>
                    <span className={`menu-connecteur-etat${e.ton === 'ambre' ? ' ambre' : ''}`}>
                      {e.texte}
                    </span>
                  </button>
                )
              })}
            </div>
          )}
        </div>
      </div>
      {c && etat && !etat.lancer && !(etat.reconnecter && c.defi) && !enCours && (
        <p className="discret panneau-message">
          {nom} : {etat.texte}.
        </p>
      )}
      {resultat && (
        <p className={`panneau-message resultat-${resultat.ton}`} role="status">
          <Icone
            nom={
              resultat.ton === 'ok'
                ? 'check_circle'
                : resultat.ton === 'alerte'
                  ? 'warning'
                  : 'search_off'
            }
            taille={16}
          />
          {resultat.texte}
        </p>
      )}
      {erreur && <p className="alerte panneau-message">{erreur}</p>}
    </div>
  )
}

/**
 * L'aperçu d'une pièce en vue : le vrai fichier à gauche ; à droite, la pièce, l'écriture du
 * cabinet, les deux tuiles d'écart et le geste (« C'est celle-ci »). ← → passent d'une candidate à
 * l'autre, Échap ferme (fenêtre modale du navigateur).
 */
function ApercuCandidates({
  b,
  op,
  vues,
  index,
  onIndex,
  onFermer,
  geste,
  choisie,
}: {
  b: BilanConnexion
  op: OperationBilan
  vues: readonly VueTriee[]
  index: number
  onIndex: (i: number) => void
  onFermer: () => void
  geste: (pieceId: string) => ReactNode
  choisie: (pieceId: string) => boolean
}) {
  const dialogue = useRef<HTMLDialogElement>(null)
  const n = vues.length
  const position = useRef(index)
  position.current = index
  useEffect(() => {
    dialogue.current?.showModal()
  }, [])
  useEffect(() => {
    function touche(e: KeyboardEvent) {
      if (n < 2 || (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight')) return
      e.preventDefault()
      onIndex((position.current + (e.key === 'ArrowRight' ? 1 : -1) + n) % n)
    }
    window.addEventListener('keydown', touche)
    return () => window.removeEventListener('keydown', touche)
  }, [n, onIndex])

  const v = vues[index]!
  const p = b.pieces[v.pieceId]
  const origine = p ? texteOrigine(p.origine) : null
  const url = `/api/pieces/${v.pieceId}/fichier`
  const g = choisie(v.pieceId) ? null : geste(v.pieceId)
  return (
    <dialog
      ref={dialogue}
      className="apercu-candidate"
      aria-label="Aperçu de la pièce"
      onClose={onFermer}
      onClick={(e) => {
        // Un clic sur le voile, hors de la fenêtre, la ferme.
        if (e.target === dialogue.current) dialogue.current?.close()
      }}
    >
      <div className="apercu-candidate-document">
        <div className="apercu-candidate-defilement">
          <PagesDuFichier
            key={v.pieceId}
            f={{ url, nom: p?.nom ?? 'Pièce', typeMime: p?.typeMime ?? 'application/pdf' }}
          />
        </div>
        {n > 1 && (
          <>
            <button
              type="button"
              className="apercu-fleche gauche"
              title="Précédente (←)"
              aria-label="Pièce précédente"
              onClick={() => onIndex((index - 1 + n) % n)}
            >
              <Icone nom="chevron_left" />
            </button>
            <button
              type="button"
              className="apercu-fleche droite"
              title="Suivante (→)"
              aria-label="Pièce suivante"
              onClick={() => onIndex((index + 1) % n)}
            >
              <Icone nom="chevron_right" />
            </button>
          </>
        )}
      </div>
      <div className="apercu-candidate-cote">
        <div className="apercu-candidate-barre">
          <span className="mono discret">
            Pièce {index + 1} / {n}
          </span>
          <a className="bouton-discret" href={url} download>
            <Icone nom="download" />
            Télécharger
          </a>
          <button
            type="button"
            className="bouton-discret"
            title="Fermer (Échap)"
            aria-label="Fermer"
            onClick={() => dialogue.current?.close()}
          >
            <Icone nom="close" />
          </button>
        </div>
        <div className="apercu-candidate-corps">
          <div className="apercu-candidate-piece">
            <strong>{p?.emetteur ?? p?.nom ?? 'Pièce'}</strong>
            {p?.numero && <span className="mono discret">n° {p.numero}</span>}
            {origine && (
              <span className="discret candidate-sous">
                <Icone nom={origine.icone} taille={14} />
                {origine.texte}
              </span>
            )}
            <Link to="/pieces/$id" params={{ id: v.pieceId }}>
              Fiche de la pièce
            </Link>
          </div>
          <div className="panneau-rappel apercu-rappel">
            <span className="panneau-sous-titre">Écriture {b.connexion.portail}</span>
            <div className="apercu-rappel-ligne">
              <strong>{op.marchand ?? op.libelle}</strong>
              <span className="mono">{jourCourt(op.date, true)}</span>
              <span className="mono panneau-rappel-montant">
                {formaterMontant(op.montantCts, op.devise)}
              </span>
            </div>
          </div>
          {v.ecart && <TuilesEcart ecart={v.ecart} piece={p} devise={op.devise} />}
        </div>
        <div className="apercu-candidate-pied">
          {choisie(v.pieceId) ? (
            <span className="candidate-choisie grand">
              <Icone nom="check_circle" plein />
              Choisie
            </span>
          ) : (
            g
          )}
          <span className="discret apercu-clavier">
            <kbd>←</kbd> <kbd>→</kbd> pièce précédente / suivante · <kbd>Échap</kbd> fermer
          </span>
        </div>
      </div>
    </dialog>
  )
}
